/**
 * Tombstones: the record of a delete, kept so the delete reaches the other
 * device instead of the row coming back from it (.specs/device-sync.md).
 *
 * Every delete of a synced row goes through this file, whether the user made
 * it here (repository.ts) or it arrived from the other device (sqliteSync.ts).
 */

import { and, eq, inArray, lt, or, sql } from 'drizzle-orm';
import type { AnySQLiteTable, SQLiteColumn } from 'drizzle-orm/sqlite-core';

import {
  DEFAULT_SYNC_ORDER,
  SYNC_TABLES,
  type SyncTableName,
  type SyncTombstone
} from '../helpers/syncTypes';
import * as schema from './schema';
import type { DrizzleDB } from './types';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// A tombstone older than this is dropped (pruneTombstones). A device that has
// not synced for longer can bring the row back.
export const TOMBSTONE_RETENTION_DAYS = 180;
export const TOMBSTONE_RETENTION_MS = TOMBSTONE_RETENTION_DAYS * MS_PER_DAY;

// Bound parameters per statement: SQLite caps them, and a sync can carry
// thousands of ids.
const ID_CHUNK_SIZE = 500;
const TOMBSTONE_INSERT_CHUNK_SIZE = 100;

// Every synced table has `id` and `updatedAt` (baseColumns); the rest of the
// shape differs per table.
type SyncedTable = AnySQLiteTable & {
  id: SQLiteColumn;
  updatedAt: SQLiteColumn;
};

export const SYNCED_TABLES: Record<SyncTableName, SyncedTable> = {
  [SYNC_TABLES.PRIVATE_POSTS]: schema.privatePosts,
  [SYNC_TABLES.PRIVATE_UPLOADS]: schema.privateUploads,
  [SYNC_TABLES.PRIVATE_POST_FACTIII_COUNTS]: schema.privatePostFactiiiCounts,
  [SYNC_TABLES.PRIVATE_POST_FACTIIIS]: schema.privatePostFactiiis,
  [SYNC_TABLES.PRIVATE_POST_FACTIII_UPLOADS]: schema.privatePostFactiiiUploads,
  [SYNC_TABLES.SPACE_TIMES]: schema.spaceTimes,
  [SYNC_TABLES.POST_ENGAGEMENTS]: schema.postEngagements,
  [SYNC_TABLES.FACTIII_ENGAGEMENTS]: schema.factiiiEngagements,
  [SYNC_TABLES.MOOD_ENTRIES]: schema.moodEntries
};

export const isSyncTableName = (name: string): name is SyncTableName =>
  Object.prototype.hasOwnProperty.call(SYNCED_TABLES, name);

export interface RowRef {
  table: SyncTableName;
  id: string;
}

// A SyncTombstone whose table names are known to this version.
export interface Tombstone extends SyncTombstone {
  table: SyncTableName;
  rootTable: SyncTableName;
}

/** What a delete leaves for the caller to clean up outside the database. */
export interface DeletedRecords {
  /** Blob storage keys of the deleted uploads. */
  blobKeys: string[];
  /** Local data hash ids: the deleted posts' snapshots, plus the blob keys. */
  hashIds: string[];
}

interface ParentLink {
  child: SyncTableName;
  /** The foreign key, as the column and as the property of a selected row. */
  column: SQLiteColumn;
  field: string;
  parent: SyncTableName;
  /** ON DELETE CASCADE. False for SET NULL: the child outlives its parent. */
  cascades: boolean;
}

// The foreign keys between synced tables (schema/posts.ts). Kept beside the
// tombstones because a delete has to name every row the database removes with
// it, and SQLite does not report what a cascade took.
const PARENT_LINKS: ParentLink[] = [
  {
    child: SYNC_TABLES.PRIVATE_POSTS,
    column: schema.privatePosts.parentPostId,
    field: 'parentPostId',
    parent: SYNC_TABLES.PRIVATE_POSTS,
    cascades: true
  },
  {
    child: SYNC_TABLES.PRIVATE_UPLOADS,
    column: schema.privateUploads.postId,
    field: 'postId',
    parent: SYNC_TABLES.PRIVATE_POSTS,
    cascades: false
  },
  {
    child: SYNC_TABLES.PRIVATE_POST_FACTIII_COUNTS,
    column: schema.privatePostFactiiiCounts.postId,
    field: 'postId',
    parent: SYNC_TABLES.PRIVATE_POSTS,
    cascades: true
  },
  {
    child: SYNC_TABLES.PRIVATE_POST_FACTIIIS,
    column: schema.privatePostFactiiis.postFactiiiCountId,
    field: 'postFactiiiCountId',
    parent: SYNC_TABLES.PRIVATE_POST_FACTIII_COUNTS,
    cascades: true
  },
  {
    child: SYNC_TABLES.PRIVATE_POST_FACTIII_UPLOADS,
    column: schema.privatePostFactiiiUploads.postFactiiiId,
    field: 'postFactiiiId',
    parent: SYNC_TABLES.PRIVATE_POST_FACTIIIS,
    cascades: true
  },
  {
    child: SYNC_TABLES.PRIVATE_POST_FACTIII_UPLOADS,
    column: schema.privatePostFactiiiUploads.uploadId,
    field: 'uploadId',
    parent: SYNC_TABLES.PRIVATE_UPLOADS,
    cascades: true
  },
  {
    child: SYNC_TABLES.SPACE_TIMES,
    column: schema.spaceTimes.postFactiiiId,
    field: 'postFactiiiId',
    parent: SYNC_TABLES.PRIVATE_POST_FACTIIIS,
    cascades: true
  }
];

// Rows by table, each with its `updatedAt`.
type RowSet = Map<SyncTableName, Map<string, number>>;
type RowStamp = { id: string; updatedAt: number };

export const tombstoneKey = (table: string, id: string): string =>
  `${table}:${id}`;

const rootOf = (tombstone: Tombstone): RowRef => ({
  table: tombstone.rootTable,
  id: tombstone.rootId
});

const noDeletedRecords = (): DeletedRecords => ({ blobKeys: [], hashIds: [] });

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function groupIdsByTable(rows: RowRef[]): Map<SyncTableName, string[]> {
  const byTable = new Map<SyncTableName, string[]>();
  for (const row of rows) {
    byTable.set(row.table, [...(byTable.get(row.table) ?? []), row.id]);
  }
  return byTable;
}

async function selectStamps(
  db: DrizzleDB,
  table: SyncedTable,
  column: SQLiteColumn,
  values: string[]
): Promise<RowStamp[]> {
  const stamps: RowStamp[] = [];
  for (const part of chunk(values, ID_CHUNK_SIZE)) {
    const rows = (await db
      .select({ id: table.id, updatedAt: table.updatedAt })
      .from(table)
      .where(inArray(column, part))) as RowStamp[];
    stamps.push(...rows);
  }
  return stamps;
}

// Adds the rows not yet in the set and returns their ids.
function addToRowSet(
  rows: RowSet,
  table: SyncTableName,
  stamps: RowStamp[]
): string[] {
  const known = rows.get(table) ?? new Map<string, number>();
  rows.set(table, known);
  const added: string[] = [];
  for (const { id, updatedAt } of stamps) {
    if (known.has(id)) continue;
    known.set(id, updatedAt);
    added.push(id);
  }
  return added;
}

/** Which of these rows exist, with their `updatedAt`. */
async function findRows(db: DrizzleDB, rows: RowRef[]): Promise<RowSet> {
  const found: RowSet = new Map();
  for (const [tableName, ids] of groupIdsByTable(rows)) {
    const table = SYNCED_TABLES[tableName];
    addToRowSet(found, tableName, await selectStamps(db, table, table.id, ids));
  }
  return found;
}

/**
 * The rows the database loses when `rows` are deleted: those of `rows` that
 * exist, and every synced row a foreign key cascades the delete to.
 */
async function collectCascade(db: DrizzleDB, rows: RowRef[]): Promise<RowSet> {
  const found = await findRows(db, rows);
  let frontier = new Map<SyncTableName, string[]>();
  for (const [tableName, known] of found) {
    frontier.set(tableName, [...known.keys()]);
  }

  while (frontier.size > 0) {
    const next = new Map<SyncTableName, string[]>();
    for (const link of PARENT_LINKS) {
      const parentIds = frontier.get(link.parent);
      if (!link.cascades || !parentIds || parentIds.length === 0) continue;

      const children = await selectStamps(
        db,
        SYNCED_TABLES[link.child],
        link.column,
        parentIds
      );
      const added = addToRowSet(found, link.child, children);
      if (added.length > 0) {
        next.set(link.child, [...(next.get(link.child) ?? []), ...added]);
      }
    }
    frontier = next;
  }

  return found;
}

const countRows = (rows: RowSet): number =>
  [...rows.values()].reduce((total, known) => total + known.size, 0);

const newestUpdate = (rows: RowSet): number =>
  Math.max(0, ...[...rows.values()].flatMap((known) => [...known.values()]));

// Read before the rows go: afterwards nothing says which blobs and snapshot
// hashes belonged to them.
async function collectDeletedRecords(
  db: DrizzleDB,
  rows: RowSet
): Promise<DeletedRecords> {
  const uploadIds = [...(rows.get(SYNC_TABLES.PRIVATE_UPLOADS)?.keys() ?? [])];
  const postIds = [...(rows.get(SYNC_TABLES.PRIVATE_POSTS)?.keys() ?? [])];

  const blobKeys: string[] = [];
  for (const part of chunk(uploadIds, ID_CHUNK_SIZE)) {
    const uploads = await db
      .select({ key: schema.privateUploads.key })
      .from(schema.privateUploads)
      .where(inArray(schema.privateUploads.id, part));
    blobKeys.push(...uploads.map((upload) => upload.key));
  }

  const snapshotIds: string[] = [];
  for (const part of chunk(postIds, ID_CHUNK_SIZE)) {
    const snapshots = await db
      .select({ id: schema.privatePostSnapshots.id })
      .from(schema.privatePostSnapshots)
      .where(inArray(schema.privatePostSnapshots.postId, part));
    snapshotIds.push(...snapshots.map((snapshot) => snapshot.id));
  }

  return { blobKeys, hashIds: [...snapshotIds, ...blobKeys] };
}

/**
 * Stores tombstones. One already held for the same record is replaced only by
 * a later delete.
 */
async function writeTombstones(
  db: DrizzleDB,
  tombstones: Tombstone[]
): Promise<void> {
  for (const part of chunk(tombstones, TOMBSTONE_INSERT_CHUNK_SIZE)) {
    await db
      .insert(schema.syncTombstones)
      .values(
        part.map((tombstone) => ({
          tableName: tombstone.table,
          recordId: tombstone.id,
          deletedAt: tombstone.deletedAt,
          rootTableName: tombstone.rootTable,
          rootRecordId: tombstone.rootId
        }))
      )
      .onConflictDoUpdate({
        target: [
          schema.syncTombstones.tableName,
          schema.syncTombstones.recordId
        ],
        set: {
          deletedAt: sql`excluded.deleted_at`,
          rootTableName: sql`excluded.root_table_name`,
          rootRecordId: sql`excluded.root_record_id`
        },
        setWhere: sql`excluded.deleted_at > ${schema.syncTombstones.deletedAt}`
      });
  }
}

async function removeTombstones(db: DrizzleDB, rows: RowRef[]): Promise<void> {
  for (const [tableName, ids] of groupIdsByTable(rows)) {
    for (const part of chunk(ids, ID_CHUNK_SIZE)) {
      await db
        .delete(schema.syncTombstones)
        .where(
          and(
            eq(schema.syncTombstones.tableName, tableName),
            inArray(schema.syncTombstones.recordId, part)
          )
        );
    }
  }
}

// Children before parents, so no statement depends on a cascade and none
// trips a foreign key.
async function removeRows(db: DrizzleDB, rows: RowSet): Promise<void> {
  for (const tableName of [...DEFAULT_SYNC_ORDER].reverse()) {
    const ids = [...(rows.get(tableName)?.keys() ?? [])];
    const table = SYNCED_TABLES[tableName];
    for (const part of chunk(ids, ID_CHUNK_SIZE)) {
      await db.delete(table).where(inArray(table.id, part));
    }
  }
}

/**
 * Writes a tombstone for every row of `rows`, all belonging to the delete of
 * `root`, then deletes the rows.
 *
 * The two are separate statements: the web driver (sqlite-proxy through
 * tabWriter.ts) has no transaction a repository call can open, and BEGIN and
 * COMMIT as separate statements are what device-sync.md forbids. So the order
 * carries the guarantee instead: tombstones first. A crash in between leaves a
 * tombstone whose row is still here, never a deleted row without a tombstone,
 * and the next sync finishes the delete (applyTombstones on the local set).
 */
async function tombstoneAndRemove(
  db: DrizzleDB,
  root: RowRef,
  rows: RowSet,
  deletedAt: number
): Promise<DeletedRecords> {
  if (countRows(rows) === 0) return noDeletedRecords();

  const deleted = await collectDeletedRecords(db, rows);
  const tombstones: Tombstone[] = [];
  for (const [table, known] of rows) {
    for (const id of known.keys()) {
      tombstones.push({
        table,
        id,
        deletedAt,
        rootTable: root.table,
        rootId: root.id
      });
    }
  }

  await writeTombstones(db, tombstones);
  await removeRows(db, rows);
  return deleted;
}

/**
 * Deletes a record the user deleted, with a tombstone for it and for every
 * synced row that goes with it: the rows a foreign key cascades to, and
 * `attached`, rows the caller deletes alongside that no cascade reaches (a
 * post's uploads).
 */
export async function deleteWithTombstones(
  db: DrizzleDB,
  root: RowRef,
  attached: RowRef[] = []
): Promise<DeletedRecords> {
  const rows = await collectCascade(db, [root, ...attached]);
  return tombstoneAndRemove(db, root, rows, Date.now());
}

/** deleteWithTombstones for several unrelated records: each its own delete. */
export async function deleteEachWithTombstones(
  db: DrizzleDB,
  roots: RowRef[]
): Promise<DeletedRecords> {
  const deleted = noDeletedRecords();
  for (const root of roots) {
    const { blobKeys, hashIds } = await deleteWithTombstones(db, root);
    deleted.blobKeys.push(...blobKeys);
    deleted.hashIds.push(...hashIds);
  }
  return deleted;
}

export async function getTombstones(db: DrizzleDB): Promise<Tombstone[]> {
  const rows = await db.select().from(schema.syncTombstones);
  const tombstones: Tombstone[] = [];
  for (const row of rows) {
    if (!isSyncTableName(row.tableName)) continue;
    if (!isSyncTableName(row.rootTableName)) continue;
    tombstones.push({
      table: row.tableName,
      id: row.recordId,
      deletedAt: row.deletedAt,
      rootTable: row.rootTableName,
      rootId: row.rootRecordId
    });
  }
  return tombstones;
}

/** Drops tombstones past TOMBSTONE_RETENTION_MS. */
export async function pruneTombstones(
  db: DrizzleDB,
  now: number = Date.now()
): Promise<void> {
  await db
    .delete(schema.syncTombstones)
    .where(lt(schema.syncTombstones.deletedAt, now - TOMBSTONE_RETENTION_MS));
}

/** Forgets every tombstone. Only for a wipe of the whole database. */
export async function clearTombstones(db: DrizzleDB): Promise<void> {
  await db.delete(schema.syncTombstones);
}

/**
 * Forgets the delete of `root`: its tombstone and those of every row that
 * went with it. Called when a later edit of `root` arrives, which undoes the
 * delete as a whole.
 */
export async function clearTombstoneGroup(
  db: DrizzleDB,
  root: RowRef
): Promise<void> {
  await db
    .delete(schema.syncTombstones)
    .where(
      or(
        and(
          eq(schema.syncTombstones.tableName, root.table),
          eq(schema.syncTombstones.recordId, root.id)
        ),
        and(
          eq(schema.syncTombstones.rootTableName, root.table),
          eq(schema.syncTombstones.rootRecordId, root.id)
        )
      )
    );
}

/**
 * The rows an incoming record points at through a foreign key. If one of them
 * was refused, the record cannot be stored either.
 */
export function parentRefs(
  table: SyncTableName,
  data: Record<string, unknown>
): RowRef[] {
  const parents: RowRef[] = [];
  for (const link of PARENT_LINKS) {
    const parentId = data[link.field];
    if (link.child === table && typeof parentId === 'string') {
      parents.push({ table: link.parent, id: parentId });
    }
  }
  return parents;
}

// A tombstone from the wire, or null when this version cannot act on it (a
// table it does not sync, or a malformed entry).
function toTombstone(wire: SyncTombstone): Tombstone | null {
  if (!isSyncTableName(wire.table) || typeof wire.id !== 'string') return null;
  if (!Number.isFinite(wire.deletedAt)) return null;

  const hasRoot =
    isSyncTableName(wire.rootTable) && typeof wire.rootId === 'string';
  return {
    table: wire.table,
    id: wire.id,
    deletedAt: wire.deletedAt,
    rootTable: hasRoot ? (wire.rootTable as SyncTableName) : wire.table,
    rootId: hasRoot ? wire.rootId : wire.id
  };
}

/**
 * Applies tombstones to this database: the other device's, or this device's
 * own (which finishes a delete that a crash interrupted).
 *
 * The conflict rule is last writer wins, per delete rather than per row:
 * - A delete is its root record and every row that went with it. When the root
 *   is still here, the whole delete is decided at once.
 * - It is carried out unless the root, or any row the delete would take here,
 *   was updated after `deletedAt`. Then nothing is deleted and none of its
 *   tombstones are kept: the later edit wins. If the newer row is not the root
 *   itself, the root's `updatedAt` is moved up to it, so the root travels back
 *   as newer than the delete and the device that deleted restores the lot.
 * - When the root is not here, each remaining row is decided on its own.
 * - Equal timestamps: the delete wins.
 *
 * Returns the blobs and hashes of what was deleted, for the caller to clean
 * up.
 */
export async function applyTombstones(
  db: DrizzleDB,
  incoming: SyncTombstone[]
): Promise<DeletedRecords> {
  const byKey = new Map<string, Tombstone>();
  for (const wire of incoming) {
    const tombstone = toTombstone(wire);
    if (!tombstone) continue;
    const key = tombstoneKey(tombstone.table, tombstone.id);
    const seen = byKey.get(key);
    if (!seen || tombstone.deletedAt > seen.deletedAt) {
      byKey.set(key, tombstone);
    }
  }

  const deleted = noDeletedRecords();
  if (byKey.size === 0) return deleted;

  const held = new Map<string, Tombstone>();
  for (const tombstone of await getTombstones(db)) {
    held.set(tombstoneKey(tombstone.table, tombstone.id), tombstone);
  }
  const present = await findRows(db, [...byKey.values()]);
  const isPresent = (row: RowRef) => present.get(row.table)?.has(row.id);

  // A tombstone joins its root's delete when the root's own tombstone came
  // with it; otherwise it stands alone.
  const deletes = new Map<string, Tombstone[]>();
  for (const [key, tombstone] of byKey) {
    const rootKey = tombstoneKey(tombstone.rootTable, tombstone.rootId);
    const deleteKey = byKey.has(rootKey) ? rootKey : key;
    deletes.set(deleteKey, [...(deletes.get(deleteKey) ?? []), tombstone]);
  }

  const toStore: Tombstone[] = [];
  for (const [deleteKey, members] of deletes) {
    const root = byKey.get(deleteKey);
    const here = members.filter(isPresent);
    const rootIsHere =
      root !== undefined && members.includes(root) && here.includes(root);

    // With the root here, one decision covers every tombstone of the delete.
    // Without it, each row still here is decided alone, and the tombstones of
    // rows already gone are simply stored.
    const decisions = rootIsHere
      ? [{ head: root, tombstones: members, rows: here }]
      : here.map((tombstone) => ({
          head: tombstone,
          tombstones: [tombstone],
          rows: [tombstone]
        }));
    if (!rootIsHere) {
      toStore.push(...members.filter((tombstone) => !isPresent(tombstone)));
    }

    for (const { head, tombstones, rows } of decisions) {
      const cascade = await collectCascade(db, rows);
      const newest = newestUpdate(cascade);

      if (newest > head.deletedAt) {
        await removeTombstones(
          db,
          tombstones.filter((tombstone) =>
            held.has(tombstoneKey(tombstone.table, tombstone.id))
          )
        );
        const headUpdatedAt = present.get(head.table)?.get(head.id) ?? 0;
        if (rootIsHere && headUpdatedAt <= head.deletedAt) {
          const table = SYNCED_TABLES[head.table];
          await db
            .update(table)
            .set({ updatedAt: newest })
            .where(eq(table.id, head.id));
        }
        continue;
      }

      const removed = await tombstoneAndRemove(
        db,
        rootOf(head),
        cascade,
        head.deletedAt
      );
      deleted.blobKeys.push(...removed.blobKeys);
      deleted.hashIds.push(...removed.hashIds);
      toStore.push(...tombstones);
    }
  }

  await writeTombstones(
    db,
    toStore.filter((tombstone) => {
      const mine = held.get(tombstoneKey(tombstone.table, tombstone.id));
      return !mine || tombstone.deletedAt > mine.deletedAt;
    })
  );

  return deleted;
}
